import type { Point3 } from '../core/GridSnap';

/**
 * Pure numeric shaping for `SnapIndicator`'s own movement between grid cells — kept separate
 * from `SnapIndicator`'s Babylon plumbing (the mesh, the render loop) the same split
 * `GridSnap`/`FadeAnimation`/`AssistantLightAnimation` already use, so this is testable without
 * a headless renderer.
 *
 * The owner's own framing, from a headset test of the indicator's first cut (which jumped
 * instantly between cells): *"they need to either visibly travel between spots or at the very
 * least fade a bit when they move. I prefer them moving quickly to the next spot so it's easier
 * to keep track of where it will land when you move it."* So this is a short, eased slide from
 * wherever the indicator currently is to wherever it should be now — quick enough that it always
 * reads as being at (or just arriving at) the target, slow enough that the eye can follow which
 * way it went.
 *
 * `travelPositionAt` takes `from`/`to` fresh on every call rather than owning any state itself —
 * the caller (`SnapIndicator`) is the one that knows whether a new travel should start from the
 * indicator's *previous* target (an ordinary retarget) or from wherever it is *right now*
 * mid-slide (a retarget that interrupts an in-progress one); this module only ever answers "where
 * is a slide between two points at this elapsed time", which is the same question either way.
 */

/** How long a slide between two cells takes. Short on purpose — the owner asked for quick
 *  movement, not a lingering glide, so the indicator never lags noticeably behind a fast-moving
 *  hand while it is still catching up to the previous target. */
export const TRAVEL_DURATION_MS = 120;

/** One step of a slide between two cells. */
export interface TravelStep {
  /** The position to paint this instant. */
  readonly position: Point3;
  /** Whether the slide has reached `to` and needs no further ticking. */
  readonly done: boolean;
}

/** Ease-out cubic — starts fast and settles into the target rather than arriving at a constant
 *  or accelerating rate, so a fast hand movement reads as the indicator "catching up" smoothly
 *  rather than snapping to a stop. */
function ease(t: number): number {
  const clamped = Math.min(1, Math.max(0, t));
  return 1 - (1 - clamped) ** 3;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * The position `elapsedMs` after a slide from `from` to `to` began, over `durationMs`. Reaches
 * `to` exactly (not merely close to it) once `elapsedMs >= durationMs` — the mesh must actually
 * land on the target cell's centre, not asymptotically approach it and stop a hair short.
 */
export function travelPositionAt(
  elapsedMs: number,
  from: Point3,
  to: Point3,
  durationMs: number = TRAVEL_DURATION_MS,
): TravelStep {
  if (elapsedMs >= durationMs) {
    return { position: to, done: true };
  }

  const t = ease(elapsedMs / durationMs);

  return {
    position: {
      x: lerp(from.x, to.x, t),
      y: lerp(from.y, to.y, t),
      z: lerp(from.z, to.z, t),
    },
    done: false,
  };
}
